// Template-based methods for TheoryCoach
// Extracted to reduce main file size and prevent Cursor crashes


/**
 * Template-based fallback for suggestions (when AI unavailable)
 * PROMPT MATCH: Different cards for correct vs incorrect answers
 */
export const getTemplateSuggestions = (
  language: string,
  questionText?: string,
  subject?: string,
  hasImage?: boolean,
  genericCard: string = 'I guessed',
  isCorrect?: boolean, // PROMPT MATCH: For adaptive follow-up logic
  roundNumber?: number // PROMPT MATCH: Round 1 (reasoning), Round 2 (misconception), Round 3 (reinforcement)
): string[] => {
  // PROMPT MATCH: Three rounds with different focus
  // Round 1: general reasoning reflection
  // Round 2: misconception clarification
  // Round 3: reinforcement / focus-area guidance
  const round = roundNumber || 1;
  
  if (isCorrect) {
    // Motivational / light reflection cards for correct answers
    if (round === 1) {
      // Round 1: General reasoning reflection
      if (language === 'nl') {
        return ['Wat maakte dit goed?', 'Welke regel paste ik toe?'];
      }
      if (language === 'ar') {
        return ['ما الذي جعل هذا صحيحاً؟', 'أي قاعدة طبقتها؟'];
      }
      return ['What made this right?', 'Which rule did I apply?'];
    } else if (round === 2) {
      // Round 2: Misconception clarification (for correct, this is deeper understanding)
      if (language === 'nl') {
        return ['Waarom was dit correct?', 'Wanneer geldt deze regel?'];
      }
      if (language === 'ar') {
        return ['لماذا كان هذا صحيحاً؟', 'متى تنطبق هذه القاعدة؟'];
      }
      return ['Why was this correct?', 'When does this rule apply?'];
    } else {
      // Round 3: Reinforcement / focus-area guidance
      if (language === 'nl') {
        return ['Ik begrijp dit nu', 'Klaar voor examen'];
      }
      if (language === 'ar') {
        return ['أفهم هذا الآن', 'جاهز للامتحان'];
      }
      return ['I understand this now', 'Ready for exam'];
    }
  }
  
  // Target misconception / reasoning prompt cards for incorrect answers
  const text = `${questionText || ''} ${subject || ''}`.toLowerCase();
  const subj = (subject || '').toLowerCase();

  const _isTrafficLight = subj.includes('traffic lights') || subj.includes('traffic light') || text.includes('traffic light') || text.includes('verkeerslicht');
  const _isSign = hasImage || subj.includes('sign') || subj.includes('road information') || text.includes('sign') || text.includes('verkeersbord');
  const _isPriority = subj.includes('priority') || subj.includes('roundabout') || subj.includes('tram') || subj.includes('pedestrian') || text.includes('priority') || text.includes('right of way') || text.includes('voorrang');
  const _isSpeed = subj.includes('speed') || text.includes('speed') || text.includes('snelheid');
  const _isParking = subj.includes('parking') || text.includes('parking');
  const _isHazard = subj.includes('hazard') || text.includes('hazard');
  const _isWeather = subj.includes('weather') || text.includes('weather');
  const _isEmergency = subj.includes('emergency') || text.includes('emergency');
  const _isAlcohol = subj.includes('alcohol') || subj.includes('drugs') || text.includes('alcohol') || text.includes('drugs');
  const _isFatigue = subj.includes('fatigue') || subj.includes('rest') || text.includes('fatigue');
  const _isVehicle = subj.includes('vehicle') || text.includes('vehicle');
  const _isTech = subj.includes('technology') || text.includes('technology') || text.includes('safety system');
  const _isEnvironmental = subj.includes('environmental') || text.includes('environment');

  // PROMPT MATCH: Target misconception / reasoning prompt cards
  if (round === 1) {
    // Round 1: General reasoning reflection
    if (language === 'nl') {
      return ['Ik las de vraag verkeerd', genericCard];
    }
    if (language === 'ar') {
      return ['قرأت السؤال بشكل خاطئ', genericCard];
    }
    return ['I misread the question', genericCard];
  } else if (round === 2) {
    // Round 2: Misconception clarification
    if (language === 'nl') {
      return ['Wat begreep ik verkeerd?', 'Welk deel verwarde me?'];
    }
    if (language === 'ar') {
      return ['ماذا فهمت بشكل خاطئ؟', 'أي جزء أربكني؟'];
    }
    return ['What did I misunderstand?', 'Which part confused me?'];
  } else {
    // Round 3: Reinforcement / focus-area guidance
    if (language === 'nl') {
      return ['Wat moet ik onthouden?', 'Klaar om door te gaan'];
    }
    if (language === 'ar') {
      return ['ماذا يجب أن أتذكر؟', 'جاهز للمتابعة'];
    }
    return ['What should I remember?', 'Ready to move on'];
  }
};

/**
 * Legacy method - kept for backward compatibility
 * Now calls async AI method with fallback
 */
export const getFollowUpSuggestions = (params: {
  language: string;
  questionText?: string;
  subject?: string;
  hasImage?: boolean;
}): string[] => {
  // Return template-based suggestions synchronously (for immediate display)
  // AI-generated suggestions will be loaded asynchronously
  return getTemplateSuggestions(
    params.language,
    params.questionText,
    params.subject,
    params.hasImage
  );
};

export const getReflectionPromptForQuestion = (params: {
  language: string;
  questionText?: string;
  subject?: string;
  hasImage?: boolean;
}): string => {
  const { language, questionText, subject, hasImage } = params;
  const text = `${questionText || ''} ${subject || ''}`.toLowerCase();
  const subj = (subject || '').toLowerCase();
  const isTrafficLight = subj.includes('traffic lights') || text.includes('traffic light') || text.includes('verkeerslicht');
  const isSign = hasImage || subj.includes('sign') || text.includes('sign') || text.includes('verkeersbord');
  const isPriority = subj.includes('priority') || subj.includes('roundabout') || subj.includes('tram') || subj.includes('pedestrian') || text.includes('priority') || text.includes('right of way') || text.includes('voorrang');
  const isSpeed = subj.includes('speed') || text.includes('speed') || text.includes('snelheid');

  if (language === 'nl') {
    if (isTrafficLight) return 'Welke kleur of pijl las je hier verkeerd?';
    if (isSign) return 'Welk borddetail miste je?';
    if (isPriority) return 'Welke voorrangsregel had je in je hoofd?';
    if (isSpeed) return 'Welke snelheidsregel paste je toe?';
    return 'Welke regel paste je toe?';
  }
  if (language === 'ar') {
    if (isTrafficLight) return 'أي لون أو سهم قرأته بشكل خاطئ؟';
    if (isSign) return 'أي تفصيل من الإشارة فاتك؟';
    if (isPriority) return 'أي قاعدة أولوية طبقتها؟';
    if (isSpeed) return 'أي قاعدة سرعة طبقتها؟';
    return 'أي قاعدة طبقتها؟';
  }
  if (isTrafficLight) return 'Which light or arrow did you misread?';
  if (isSign) return 'Which sign detail did you miss?';
  if (isPriority) return 'Which priority rule did you apply?';
  if (isSpeed) return 'Which speed rule did you apply?';
  return 'Which rule did you apply here?';
};

// Note: buildTrapFocusedReply requires formatResponse, so it's moved to the main class
// to avoid circular dependencies

export const getCorrectAnswerTemplate = (language: string, explanation: string): string => {
  const templates: Record<string, string[]> = {
    en: [
      `Nice work. Correct. ${explanation}`,
      `Good job. Correct. ${explanation}`,
      `Well done. Correct. ${explanation}`
    ],
    nl: [
      `Goed gedaan. Correct. ${explanation}`,
      `Mooi zo. Correct. ${explanation}`,
      `Sterk gedaan. Correct. ${explanation}`
    ],
    ar: [
      `أحسنت. صحيح. ${explanation}`,
      `عمل رائع. صحيح. ${explanation}`,
      `جيد جداً. صحيح. ${explanation}`
    ]
  };
  const langTemplates = templates[language] || templates.en;
  return langTemplates[Math.floor(Math.random() * langTemplates.length)];
};

export const getIncorrectAnswerTemplate = (language: string, explanation: string): string => {
  const templates: Record<string, string[]> = {
    en: [
      `Almost. ${explanation}`,
      `Close. ${explanation}`,
      `Not quite. ${explanation}`
    ],
    nl: [
      `Bijna. ${explanation}`,
      `Dichtbij. ${explanation}`,
      `Niet helemaal. ${explanation}`
    ],
    ar: [
      `تقريباً. ${explanation}`,
      `قريب جداً. ${explanation}`,
      `ليس تماماً. ${explanation}`
    ]
  };
  const langTemplates = templates[language] || templates.en;
  return langTemplates[Math.floor(Math.random() * langTemplates.length)];
};

export const getFocusAreaTemplates = (language: string): string[] => {
  const templates: Record<string, string[]> = {
    en: [
      "I see you're working on [Focus Area]. Let's practice a few more questions here. Ready?",
      "You're focusing on [Focus Area] - great choice! Let's continue.",
      "[Focus Area] needs more practice. Let's work on it together."
    ],
    nl: [
      "Ik zie dat je werkt aan [Focus Area]. Laten we hier nog een paar vragen oefenen. Klaar?",
      "Je focust op [Focus Area] - goede keuze! Laten we doorgaan.",
      "[Focus Area] heeft meer oefening nodig. Laten we er samen aan werken."
    ],
    ar: [
      "أرى أنك تعمل على [Focus Area]. دعنا نتدرب على المزيد من الأسئلة هنا. هل أنت مستعد؟",
      "أنت تركز على [Focus Area] - اختيار رائع! دعنا نستمر.",
      "[Focus Area] يحتاج إلى مزيد من التدريب. دعنا نعمل عليه معاً."
    ]
  };
  return templates[language] || templates.en;
};

export const getContinueTemplates = (language: string): string[] => {
  const templates: Record<string, string[]> = {
    en: [
      "Move on to the next question.",
      "Continue to the next question.",
      "You can move on."
    ],
    nl: [
      "Ga door naar de volgende vraag.",
      "Ga verder met de volgende vraag.",
      "Je kunt doorgaan."
    ],
    ar: [
      "انتقل للسؤال التالي.",
      "تابع للسؤال التالي.",
      "يمكنك المتابعة."
    ]
  };
  return templates[language] || templates.en;
};

/**
 * Summarize explanation to 1 sentence
 */
export const summarizeExplanation = (explanation: string, _language: string): string => {
  if (!explanation) return '';
  
  // Take first sentence (usually the key point)
  const firstSentence = explanation.split(/[.!?]/)[0].trim();
  if (firstSentence.length > 100) {
    // If too long, truncate
    return firstSentence.substring(0, 97) + '...';
  }
  return firstSentence;
};

export const generateTestCompletionMessage = (
  score: number,
  totalQuestions: number,
  percentage: number,
  focusAreas: any[],
  language: string,
  testId?: string,
  testName?: string
): string => {
  // Check if current test is a focus area (score < 60%)
  const isFocusArea = percentage < 60 && testId && testName;
  const currentTestName = testName || testId || 'this topic';
  const focusAreaNames = focusAreas.length > 0
    ? focusAreas.slice(0, 2).map(area => area.testName || area.testId).join(', ')
    : '';
  const focusAreaLine = isFocusArea
    ? `Focus area: ${currentTestName}.`
    : focusAreaNames
    ? `Focus areas: ${focusAreaNames}`
    : '';

  const motivation: Record<string, string> = {
    en: 'Good effort - keep going.',
    nl: 'Goed gedaan - ga door.',
    ar: 'جهد جيد - واصل.'
  };

  const templates: Record<string, string[]> = {
    en: [
      `That's the end of this test.\nYour score: ${percentage}% (${score}/${totalQuestions} correct)\n${focusAreaLine ? `${focusAreaLine}` : ''}\n${motivation.en}\n\nWant to continue or focus on what you missed?`,
      `That's the end of this test.\nScore: ${percentage}% (${score}/${totalQuestions} correct)\n${focusAreaLine ? `${focusAreaLine}` : ''}\n${motivation.en}\n\nWant to continue or focus on what you missed?`
    ],
    nl: [
      `Dat is het einde van deze test.\nJe score: ${percentage}% (${score}/${totalQuestions} correct)\n${focusAreaLine ? focusAreaLine.replace('Focus area', 'Focusgebied').replace('Focus areas', 'Focusgebieden') : ''}\n${motivation.nl}\n\nWil je doorgaan of focussen op wat je gemist hebt?`,
      `Dat is het einde van deze test.\nScore: ${percentage}% (${score}/${totalQuestions} correct)\n${focusAreaLine ? focusAreaLine.replace('Focus area', 'Focusgebied').replace('Focus areas', 'Focusgebieden') : ''}\n${motivation.nl}\n\nWil je doorgaan of focussen op wat je gemist hebt?`
    ],
    ar: [
      `انتهى هذا الاختبار.\nنتيجتك: ${percentage}% (${score}/${totalQuestions} صحيح)\n${focusAreaLine ? focusAreaLine.replace('Focus area', 'مجال تركيز').replace('Focus areas', 'مجالات التركيز') : ''}\n${motivation.ar}\n\nهل تريد المتابعة أم التركيز على ما فاتك؟`,
      `انتهى هذا الاختبار.\nالنتيجة: ${percentage}% (${score}/${totalQuestions} صحيح)\n${focusAreaLine ? focusAreaLine.replace('Focus area', 'مجال تركيز').replace('Focus areas', 'مجالات التركيز') : ''}\n${motivation.ar}\n\nهل تريد المتابعة أم التركيز على ما فاتك؟`
    ]
  };
  const langTemplates = templates[language] || templates.en;
  return langTemplates[Math.floor(Math.random() * langTemplates.length)];
};
